import moment from 'moment';

import {
  isMultipleChoice,
  isTextEntry,
  isRank,
  isNumericEntry,
  isMultipleInput,
  sanitizeText,
  striptagsAndUnescapeText,
  isConstantSum,
  isNumericMultiple,
  areFrontendIdsEqual,
} from './helpers';
import Utils from '../../utils/utils';

export const getTranslationOfSelectedLanguage = (state = {}, selectedLanguage) => {
  const translation =
    state.activeProject &&
    state.activeProject.translations &&
    state.activeProject.translations.data &&
    state.activeProject.translations.data[selectedLanguage]
      ? state.activeProject.translations.data[selectedLanguage]
      : {};
  return {
    items: {},
    ...translation,
  };
};

const getValuesFromAnswer = (answer) => {
  if (!answer) {
    return null;
  }

  if (Array.isArray(answer)) {
    const lastIndex = answer.length - 1;
    return answer[lastIndex].values;
  }
  return answer.values;
};

const hasAnswerValues = (answers, frontendId) => {
  return answers[frontendId] && answers[frontendId].values;
};

const hasAnswerValue = (answers, frontendId) => {
  return answers[frontendId] && (answers[frontendId].value || answers[frontendId].value === 0);
};

const transformAnswersForTextNumericEntries = ({ frontendId, question, answers, transformedAnswers }) => {
  if (!hasAnswerValue(answers, frontendId)) {
    return transformedAnswers;
  }

  transformedAnswers[frontendId] = [];

  let value = answers[frontendId].value;
  if (isTextEntry(question)) {
    value = striptagsAndUnescapeText(answers[frontendId].value);
  }

  transformedAnswers[frontendId].push(value);

  return transformedAnswers;
};

const transformAnswersForMultipleChoice = ({
  frontendId,
  question,
  answers,
  options,
  loopingOptionFrontendId,
  catalog,
  translatedCatalog,
  itemsTranslated,
  transformedAnswers,
}) => {
  if (!hasAnswerValues(answers, frontendId)) {
    return transformedAnswers;
  }
  transformedAnswers[frontendId] = [];
  const values = getValuesFromAnswer(answers[frontendId]);
  values.forEach((value) => {
    const optionFrontendId = question.options.find(
      (questionOption) => options[questionOption] && options[questionOption].position === value
    );
    if (
      optionFrontendId !== undefined &&
      optionFrontendId !== loopingOptionFrontendId &&
      loopingOptionFrontendId !== undefined
    ) {
      return;
    }
    const option = options[optionFrontendId];
    // TODO: Check if this is still valid, i think title is no longer being set when the option is translated
    if (itemsTranslated && itemsTranslated[optionFrontendId] && itemsTranslated[optionFrontendId].title) {
      transformedAnswers[frontendId].push(itemsTranslated[optionFrontendId].title);
      return;
    }
    if (option) {
      if (option.isOther) {
        transformedAnswers[frontendId].push(answers[frontendId].other);
        return;
      }
      const { label } = Utils.getCatalogUrlAndText(option, translatedCatalog, catalog);
      transformedAnswers[frontendId].push(label);
    }
  });
  return transformedAnswers;
};

const transformAnswersForConstantSum = ({ frontendId, question, answers, rows = {}, transformedAnswers = [] }) => {
  if (!hasAnswerValues(answers, frontendId)) {
    return transformedAnswers;
  }
  const values = getValuesFromAnswer(answers[frontendId]);
  if (values == null) {
    return transformedAnswers;
  }
  Object.keys(values).forEach((position) => {
    const value = values[position];
    const rowFrontendId = (question.rows || []).find((questionRowFrontendId) => {
      return rows[questionRowFrontendId] && +rows[questionRowFrontendId].position === +position;
    });
    const hasValue = value === 0 || value != null;
    if (!rowFrontendId || !hasValue) {
      return;
    }
    const key = `${frontendId}-${rowFrontendId}`;
    transformedAnswers[key] = [value];
  });
  return transformedAnswers;
};

const transformAnswersForNumericAndTextMultiple = ({
  frontendId,
  question,
  answers,
  rows = {},
  transformedAnswers = [],
}) => {
  const questionRows = question.rows || [];
  if (!hasAnswerValue(answers, frontendId) || questionRows.length === 0) {
    return transformedAnswers;
  }
  let value = answers[frontendId].value;
  if (value !== 0 && value == null) {
    return transformedAnswers;
  }
  questionRows.forEach((questionRowFrontendId) => {
    const row = rows[questionRowFrontendId];
    if (!row || !areFrontendIdsEqual(row.questionFrontendId, frontendId)) {
      return;
    }
    const key = `${row.itemId}-${row.questionFrontendId}`;
    if (isTextEntry(question)) {
      value = striptagsAndUnescapeText(answers[frontendId].value);
    }
    transformedAnswers[key] = [value];
  });
  return transformedAnswers;
};

const transformAnswersForRank = ({
  frontendId,
  question,
  answers,
  options,
  loopingOptionFrontendId,
  catalog,
  translatedCatalog,
  itemsTranslated,
  transformedAnswers,
}) => {
  const values = getValuesFromAnswer(answers[frontendId]);
  if (!values) {
    // when NA is selected
    return;
  }
  transformedAnswers[frontendId] = [];
  values.forEach((value) => {
    const { selection, option: optionSelected } = value;
    const optionFrontendId = question.options.find(
      (optionFrontendId) => options[optionFrontendId] && options[optionFrontendId].position === optionSelected
    );

    if (
      optionFrontendId === undefined ||
      (optionFrontendId !== loopingOptionFrontendId && loopingOptionFrontendId !== undefined)
    ) {
      return;
    }

    const option = options[optionFrontendId];
    // TODO: Check if this is still valid, i think title is no longer being set when the option is translated
    if (itemsTranslated && itemsTranslated[optionFrontendId] && itemsTranslated[optionFrontendId].title) {
      transformedAnswers[frontendId].push(itemsTranslated[optionFrontendId].title);
      transformedAnswers[`${frontendId}-${selection}`] = [itemsTranslated[optionFrontendId].title];
      return;
    }

    const { label } = Utils.getCatalogUrlAndText(option, translatedCatalog, catalog);
    transformedAnswers[frontendId].push(label);
    transformedAnswers[`${frontendId}-${selection}`] = [label];
  });

  return transformedAnswers;
};

export const getTransformedAnswers = ({
  onePage,
  answers,
  options,
  translations,
  loopingOptionFrontendId,
  catalog = {},
  rows = {},
}) => {
  const { catalog: translatedCatalog = {}, items: itemsTranslated = {} } = translations;
  const transformedAnswers = {};
  Object.keys(answers).forEach((frontendId) => {
    const question =
      onePage.find(
        (onePageQuestion) => onePageQuestion.frontendId && areFrontendIdsEqual(onePageQuestion.frontendId, frontendId)
      ) || {};
    if (isMultipleChoice(question)) {
      transformAnswersForMultipleChoice({
        frontendId,
        question,
        answers,
        options,
        loopingOptionFrontendId,
        catalog,
        translatedCatalog,
        itemsTranslated,
        transformedAnswers,
      });
      return;
    }
    if (isRank(question)) {
      transformAnswersForRank({
        frontendId,
        question,
        answers,
        options,
        loopingOptionFrontendId,
        catalog,
        translatedCatalog,
        itemsTranslated,
        transformedAnswers,
      });
      return;
    }
    if (!isMultipleInput(question) && (isTextEntry(question) || isNumericEntry(question))) {
      transformAnswersForTextNumericEntries({
        frontendId,
        question,
        answers,
        transformedAnswers,
      });
    }
    if (isConstantSum(question)) {
      transformAnswersForConstantSum({
        frontendId,
        question,
        answers,
        transformedAnswers,
        rows,
      });
    }
    if (isMultipleInput(question)) {
      transformAnswersForNumericAndTextMultiple({
        frontendId,
        question,
        answers,
        rows,
        transformedAnswers,
      });
    }
  });
  return transformedAnswers;
};

export const transformToReadable = (options = [], translatedAnd = 'and') => {
  const parsedOptions = options.map((option) => {
    return option === 0 ? option : sanitizeText(option);
  });
  const firstPart = parsedOptions.slice(0, parsedOptions.length - 1);
  const lastPart = parsedOptions[parsedOptions.length - 1];
  if (firstPart.length > 0) {
    return `${firstPart.join(', ')} ${translatedAnd.toLowerCase()} ${lastPart}`;
  }
  return lastPart;
};

// TODO: reduce max number of params
// eslint-disable-next-line max-params
export const getLoopedResponsesAndLoopingPageIndex = ({
  currentPageObj,
  responses,
  frontendId,
  pageShownCounters = {},
}) => {
  let loopingPageIndex;
  const pageIsLooped = !!currentPageObj.loopingEnabled;
  if (pageIsLooped) {
    const values = responses[frontendId] || [];
    loopingPageIndex = pageShownCounters[currentPageObj.frontendId] || 0;
    return { value: values[loopingPageIndex] || {}, loopingPageIndex };
  } else {
    return { value: responses[frontendId] || {}, loopingPageIndex };
  }
};

export const getTranslatedRows = ({ rows = [], items: translatedItems = {} }) => {
  return rows.map((row) => {
    const translatedRow = { ...row };
    if (translatedItems && translatedItems[row.frontendId]) {
      translatedRow.label = translatedItems[row.frontendId].title;
    }
    return translatedRow;
  });
};

/**
 * Shorten number to thousands, millions, billions, etc.
 * http://en.wikipedia.org/wiki/Metric_prefix (same as on core frontend)
 *
 * @param {number} num Number to shorten.
 * @param {number} [digits=0] The number of digits to appear after the decimal point.
 * @returns {string|number}
 *
 * @example
 * // returns '12.5k'
 * shortenLargeNumber(12543, 1)
 *
 * @example
 * // returns '-13k'
 * shortenLargeNumber(-12567)
 *
 * @example
 * // returns '51M'
 * shortenLargeNumber(51000000)
 *
 * @example
 * // returns 651
 * shortenLargeNumber(651)
 *
 * @example
 * // returns 0.12345
 * shortenLargeNumber(0.12345)
 */

export const metricPrefixConverter = (num, digits, forceDigits = false) => {
  const units = ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
  let decimal;

  for (let i = units.length - 1; i >= 0; i--) {
    decimal = Math.pow(1000, i + 1);

    if (num <= -decimal || num >= decimal) {
      const value = (num / decimal).toFixed(digits);
      return `${forceDigits ? value : +value}${units[i]}`;
    }
  }

  return num;
};

export const formatRelativeDate = (date) => {
  const inputDate = moment(date);
  const now = moment();

  const secondsDiff = now.diff(inputDate, 'seconds');
  const minutesDiff = now.diff(inputDate, 'minutes');
  const hoursDiff = now.diff(inputDate, 'hours');
  const daysDiff = now.diff(inputDate, 'days');
  const weeksDiff = now.diff(inputDate, 'weeks');
  const monthsDiff = now.diff(inputDate, 'months');

  // Less than 1 minute
  if (secondsDiff < 60) {
    return `${secondsDiff} second${secondsDiff !== 1 ? 's' : ''} ago`;
  }

  // Less than 1 hour
  if (minutesDiff < 60) {
    return `${minutesDiff} minute${minutesDiff !== 1 ? 's' : ''} ago`;
  }

  // Less than 1 day
  if (hoursDiff < 24) {
    return `${hoursDiff} hour${hoursDiff !== 1 ? 's' : ''} ago`;
  }

  // Less than 1 week
  if (daysDiff < 7) {
    return `${daysDiff} day${daysDiff !== 1 ? 's' : ''} ago`;
  }

  // Less than 1 month
  if (weeksDiff < 4) {
    return `${weeksDiff} week${weeksDiff !== 1 ? 's' : ''} ago`;
  }

  // Less than 1 year
  if (monthsDiff < 12) {
    return inputDate.format('MMMM D');
  }

  // 1 year or more
  return inputDate.format('MMMM D, YYYY');
};
